// ─── Goals Config ─────────────────────────────────────────────────────────────
// To add a new standard metric: append an object to `metrics`.
//
//  currentKey:  key in MOCK_GOALS for this month's actual value
//  targetKey:   key in MOCK_GOALS for the monthly target
//  format:      "number" | "currency"
//  iconName:    "phone" | "filecheck" | "dollar" | "trending" | "target"
//  colorClass:  "blue" | "purple" | "green" | "orange"

export const GOALS_CONFIG = {
  metrics: [
    {
      label:      "Discovery Calls",
      currentKey: "current_month_calls",
      targetKey:  "monthly_calls",
      format:     "number",
      iconName:   "phone",
      colorClass: "blue",
    },
    {
      label:      "Loans Closed",
      currentKey: "current_month_loans",
      targetKey:  "monthly_loans",
      format:     "number",
      iconName:   "filecheck",
      colorClass: "purple",
    },
    {
      label:      "Revenue Generated",
      currentKey: "current_month_revenue",
      targetKey:  "monthly_revenue",
      format:     "currency",
      iconName:   "dollar",
      colorClass: "green",
    },
  ],
};

// ─── Achievements list ────────────────────────────────────────────────────────
export const ACHIEVEMENTS = [
  { label: "First Close",       earned: true,  icon: "🏆", desc: "Closed your first deal"       },
  { label: "Call Machine",      earned: true,  icon: "📞", desc: "100+ calls in a month"        },
  { label: "Document Hero",     earned: false, icon: "📄", desc: "All docs signed on day 1"     },
  { label: "Double Dipper",     earned: false, icon: "💰", desc: "2 closes in one month"        },
  { label: "Training Complete", earned: false, icon: "🎓", desc: "Finished all modules"         },
  { label: "90-Day Ramped",     earned: false, icon: "🚀", desc: "Hit target in first 90 days"  },
];

// ─── Roadmap milestones (Training tab timeline) ───────────────────────────────
export const ROADMAP_MILESTONES = [
  { week: "Week 1",  label: "Orientation",      desc: "Complete company & document onboarding",          done: true  },
  { week: "Week 2",  label: "Product Training",  desc: "Finish Bridge + Term loan modules",               done: true  },
  { week: "Week 3",  label: "Shadow Calls",      desc: "Join 3 live discovery calls with your manager",  done: false },
  { week: "Week 4",  label: "First Solo Call",   desc: "Run a discovery call independently",             done: false },
  { week: "Month 2", label: "First Close",       desc: "Close your first funded loan",                   done: false },
  { week: "Month 3", label: "Ramp Complete",     desc: "Consistently hitting monthly targets",           done: false },
];

// ─── 90-Day Roadmap phases (Goals tab) ───────────────────────────────────────
export const ROADMAP_PHASES = [
  {
    phase:       "Days 1–30",
    headline:    "Learn the Machine",
    icon:        "🧠",
    color:       "#1d4ed8",
    lightColor:  "#eff6ff",
    borderColor: "#bfdbfe",
    goalLabel:   "First Deal Submitted",
    goal:        "Have at least 2 qualified term sheets sent to a borrower by Day 30",
    steps: [
      "Complete all Onboarding and Platform training courses (yes, including the quizzes)",
      "Run at least 10 practice scenarios through the Pricer",
      "Start reaching out to leads and having live conversations",
    ],
    callout: {
      type:  "tip",
      label: "Pro Tip",
      text:  "Don't wait until you feel ready to submit your first deal. The Propertunity Capital team reviews every submission and gives you feedback — your first deal is a learning experience, not a final exam.",
    },
  },
  {
    phase:       "Days 31–60",
    headline:    "Build the Muscle",
    icon:        "💪",
    color:       "#6d28d9",
    lightColor:  "#f5f3ff",
    borderColor: "#ddd6fe",
    goalLabel:   "3+ Active Deals",
    goal:        "Your pipeline should have at least 3 deals in various stages",
    steps: [
      "Complete Programs 201 courses — Fix & Flip and DSCR deep dives",
      "Price at least 5 real borrower scenarios per week",
      "Start outreach: 10 new real estate investor contacts per week",
      "Attend at least 2 Propertunity Capital team calls or trainings",
      "Get your first term sheet issued to a borrower",
    ],
    callout: {
      type:  "warning",
      label: "Rookie Mistake",
      text:  "Spending 3 weeks learning everything before talking to a single borrower. You learn by doing. The best reps at Propertunity Capital started reaching out in their first week — even before they felt ready.",
    },
  },
  {
    phase:       "Days 61–90",
    headline:    "Close and Scale",
    icon:        "🚀",
    color:       "#047857",
    lightColor:  "#f0fdf4",
    borderColor: "#bbf7d0",
    goalLabel:   "First Loan Funded",
    goal:        "On average this happens around month 3–4. Either way, it's coming.",
    steps: [
      "Fund your first deal — or be very close",
      "Build a repeatable outreach system: referral partners, social media, or direct outreach",
      "Complete Sales and Marketing training courses",
      "Establish a consistent weekly pipeline review habit",
      "Set your 6-month production goal with your team lead",
    ],
    callout: {
      type:  "tip",
      label: "Picture This",
      text:  "Day 90 — your first deal funds, and you realize the plan actually worked. That's not a fantasy. That's what happens when you follow the playbook and trust the process.",
    },
  },
];

export const ROADMAP_KEY_TAKEAWAYS = [
  { icon: "📚", text: "Days 1–30: Complete training, run 10 practice scenarios, start reaching out to leads" },
  { icon: "🔁", text: "Days 31–60: Build your pipeline to 3+ active deals and start consistent outreach" },
  { icon: "🏆", text: "Days 61–90: Fund your first deal and build a repeatable system" },
  { icon: "⚡", text: "Don't wait to feel ready — start doing while you're still learning" },
  { icon: "🤝", text: "The platform and the team are here to support you at every step" },
];